import type { ProductPlan } from '@/features/products/types';
import { formatMoney } from '@/shared/lib/money';
import { totalNowStrike } from '../domain/pricing';
import { SavingsBadge } from './savings-badge';

interface PlanPriceRowProps {
  plan: ProductPlan;
}

function savingsFromStrike(plan: ProductPlan, strike: number | null): number {
  if (plan.discountPercent && plan.discountPercent > 0) {
    return Math.round(plan.discountPercent);
  }
  if (strike != null && strike > plan.price) {
    return Math.round((1 - plan.price / strike) * 100);
  }
  return 0;
}

/** Single-plan "Total now" row — Figma 2001:3038. */
export function PlanPriceRow({ plan }: PlanPriceRowProps) {
  const strike = totalNowStrike(plan);
  const savings = savingsFromStrike(plan, strike);
  const currency = plan.currency;

  return (
    <div className="flex w-full items-center gap-2">
      <p className="shrink-0 text-base font-bold leading-5 text-mf-neutral-900">Total now:</p>
      <div className="h-px min-w-px flex-1 bg-mf-neutral-200" />
      <div className="flex shrink-0 flex-col items-end justify-center gap-0.5">
        <div className="flex items-center gap-1">
          {strike != null && (
            <span className="relative text-base font-normal leading-5 text-mf-neutral-400">
              {formatMoney(strike, currency)}
              <span
                aria-hidden
                className="absolute top-1/2 right-0 left-0 h-px -translate-y-1/2 bg-mf-error"
              />
            </span>
          )}
          <span className="text-base font-bold leading-5 text-mf-neutral-900">
            {formatMoney(plan.price, currency)}
          </span>
        </div>
        {savings > 0 && <SavingsBadge percent={savings} variant="compact" />}
      </div>
    </div>
  );
}
